import { useMemo, useState } from "react";
import { Select, SelectItem, Input, Chip } from "@heroui/react";
import { CalendarBlank } from "@phosphor-icons/react";

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shift = (d, m) => new Date(d.getFullYear(), d.getMonth() + m, 1);
const qStart = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);

export function periodRange(preset, custom) {
  const now = new Date();
  switch (preset) {
    case "last3": return { from: ym(shift(now, -2)), to: ym(now) };
    case "last6": return { from: ym(shift(now, -5)), to: ym(now) };
    case "thisq": { const s = qStart(now); return { from: ym(s), to: ym(shift(s, 2)) }; }
    case "lastq": { const s = shift(qStart(now), -3); return { from: ym(s), to: ym(shift(s, 2)) }; }
    case "ytd": return { from: `${now.getFullYear()}-01`, to: ym(now) };
    case "thisyear": return { from: `${now.getFullYear()}-01`, to: `${now.getFullYear()}-12` };
    case "lastyear": return { from: `${now.getFullYear() - 1}-01`, to: `${now.getFullYear() - 1}-12` };
    case "custom": return custom.from && custom.to ? { from: custom.from, to: custom.to } : { from: ym(shift(now, -11)), to: ym(now) };
    default: return { from: ym(shift(now, -11)), to: ym(now) };
  }
}

export function prevRange(range) {
  const [fy, fm] = range.from.split("-").map(Number); const [ty, tm] = range.to.split("-").map(Number);
  const len = (ty * 12 + tm) - (fy * 12 + fm) + 1;
  const end = new Date(fy, fm - 1 - 1, 1); const start = new Date(fy, fm - 1 - len, 1);
  return { from: ym(start), to: ym(end) };
}

export function pctChange(cur, prev) {
  if (prev == null || cur == null) return null;
  if (!prev) return cur ? 100 : 0;
  return ((cur - prev) / Math.abs(prev)) * 100;
}

const PRESETS = [["last12", "Last 12 months"], ["last6", "Last 6 months"], ["last3", "Last 3 months"], ["thisq", "This quarter"], ["lastq", "Last quarter"], ["ytd", "Year to date"], ["thisyear", "This year"], ["lastyear", "Last year"], ["custom", "Custom range"]];

const label = (k) => { const [y, m] = k.split("-"); return new Date(+y, +m - 1, 1).toLocaleString("en", { month: "short", year: "numeric" }); };

export function usePeriod() {
  const [preset, setPreset] = useState("last12");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const range = useMemo(() => periodRange(preset, custom), [preset, custom]);
  const prev = useMemo(() => prevRange(range), [range]);
  return { preset, setPreset, custom, setCustom, range, prev, presetLabel: PRESETS.find(([k]) => k === preset)?.[1], periodLabel: `${label(range.from)} – ${label(range.to)}` };
}

export function PeriodFilter({ preset, setPreset, custom, setCustom, range }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="period-filter">
      <Select size="sm" variant="bordered" radius="lg" aria-label="Period" selectedKeys={[preset]} onSelectionChange={(k) => setPreset([...k][0] || "last12")} className="sm:w-48" startContent={<CalendarBlank size={15} className="text-muted-foreground shrink-0" />} data-testid="period-preset">
        {PRESETS.map(([k, l]) => <SelectItem key={k}>{l}</SelectItem>)}
      </Select>
      {preset === "custom" && <div className="flex items-center gap-2">
        <Input size="sm" type="month" variant="bordered" radius="lg" aria-label="From" value={custom.from} onValueChange={(v) => setCustom((c) => ({ ...c, from: v }))} className="w-40" data-testid="period-from" />
        <span className="text-muted-foreground text-sm">–</span>
        <Input size="sm" type="month" variant="bordered" radius="lg" aria-label="To" value={custom.to} onValueChange={(v) => setCustom((c) => ({ ...c, to: v }))} className="w-40" data-testid="period-to" />
      </div>}
      <Chip size="sm" variant="flat" color="primary" className="font-semibold self-start sm:self-auto" data-testid="period-label">{label(range.from)} – {label(range.to)}</Chip>
    </div>
  );
}
